// SalesReportPage.tsx
import {
  addDays, addMonths, addWeeks, addYears,
  endOfDay, endOfMonth, endOfWeek, endOfYear,
  format, startOfDay, startOfMonth, startOfWeek, startOfYear,
  subDays, subMonths, subWeeks, subYears,
} from "date-fns";
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import ATMPageHeader from 'src/components/atoms/ATMPageHeader/ATMPageHeader';
import ATMPagination from 'src/components/atoms/ATMPagination/ATMPagination';
import Authorization from 'src/components/Authorization/Authorization';
import MOLFilterBar, { FilterType } from 'src/components/molecules/MOLFilterBar/MOLFilterBar';
import MOLTable, { TableHeader } from 'src/components/molecules/MOLTable/MOLTable';
import { useFilterPagination } from 'src/hooks/useFilterPagination';
import { RootState } from 'src/store';
import { useGetSalesReportByOutletQuery } from '../../service/OutletServices';
import ATMChart from 'src/components/atoms/ATMChart/ATMChart';
import { formatZonedDate } from 'src/utils/formatZonedDate';
import * as XLSX from 'xlsx';
import { useFetchData } from 'src/hooks/useFetchData';

// Report Types
const reportTypes = [
  { label: 'Sales Summary', value: 'SALES_SUMMARY' },
  { label: 'User', value: 'USER' },
  { label: 'Outlet', value: 'OUTLET' },
  { label: 'Register', value: 'REGISTER' },
  { label: 'Customer', value: 'CUSTOMER' },
  { label: 'Customer Group', value: 'CUSTOMER_GROUP' },
  { label: 'Promotion', value: 'PROMOTION' },
];

// Measure Types
const measureTypes = [
  { label: 'Avg. items per sale', value: 'AVG_ITEMS_PER_SALE' },
  { label: 'Avg. Sale value', value: 'AVG_SALE_VALUE' },
  { label: 'Customer count', value: 'CUSTOMER_COUNT' },
  { label: 'Discounted', value: 'DISCOUNTED' },
  { label: 'Discounted %', value: 'DISCOUNTED_PERCENT' },
  { label: 'Sale Count', value: 'SALE_COUNT' },
  { label: 'Sale With Customer', value: 'SALE_WITH_CUSTOMER' },
  { label: 'Revenue', value: 'REVENUE' },
];

// Comparison Types
const comparisonTypes = [
  { label: 'No comparison', value: 'NO_COMPARISON' },
  { label: 'Same Date in Previous year', value: 'SAME_DATE_PREVIOUS_YEAR' },
  { label: 'Same period in previous year', value: 'SAME_PERIOD_PREVIOUS_YEAR' },
];

// Time Period Types
const timePeriods = [
  { label: 'Daily', value: 'DAILY' },
  { label: 'Weekly', value: 'WEEKLY' },
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'Yearly', value: 'YEARLY' },
  { label: 'Custom', value: 'CUSTOM' },
];

const SalesReportPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { outlets } = useSelector((state: RootState) => state.auth);
  const [currentDate, setCurrentDate] = useState(new Date());

  const { appliedFilters, dateFilter, page, limit, orderBy, orderValue } = useFilterPagination([
    'reportType', 'outletId', 'measure', 'comparison', 'timePeriod'
  ]);

  // ✅ FIX 1: Robust Filter Value Extractor (Handles both String and Array)
  const getFilterValue = (index: number, fallback: string) => {
    const val = appliedFilters?.[index]?.value;
    if (Array.isArray(val)) return val[0] || fallback;
    return val || fallback;
  };

  const selectedReportType = getFilterValue(0, 'SALES_SUMMARY');
  const selectedOutlet = getFilterValue(1, outlets?.[0]?._id || '');
  const selectedMeasure = getFilterValue(2, 'REVENUE'); // Default to Revenue for grouped reports
  const selectedComparison = getFilterValue(3, 'NO_COMPARISON');
  const selectedTimePeriod = getFilterValue(4, 'DAILY');

  const startDate = dateFilter?.start_date || '';
  const endDate = dateFilter?.end_date || '';

  // Fetch sales report data
  const { data, isLoading, totalData, totalPages } = useFetchData(
    useGetSalesReportByOutletQuery,
    {
      body: {
        outletId: selectedOutlet,
        startDate: startDate,
        endDate: endDate,
        page,
        limit,
        sortBy: orderBy || 'revenue',
        sortOrder: orderValue || 'desc',
        reportDuration: selectedTimePeriod,
        reportType: selectedReportType,
        measure: selectedMeasure,
        comparison: selectedComparison,
      },
    }
  );

  // ✅ FIX 2: Exact Data Extraction
  const reportData = (data as any)?.invoices || [];
  const chartData = (data as any)?.chartData || null;
  
  const totalRevenue = (data as any)?.totalSalesData?.[0]?.totalSalesAmount || 0;
  const totalSalesCount = (data as any)?.totalSalesData?.[0]?.totalSalesCount || (data as any)?.totalCount || 0;
  
  // Profit aur Margin abhi backend se nahi aa rahe, toh 0 set kar rahe hain
  const totalProfit = 0; 

  // Update dates when time period changes
  useEffect(() => {
    if (!outlets?.length || selectedTimePeriod === 'CUSTOM') return;

    let newStartDate = '';
    let newEndDate = '';

    switch (selectedTimePeriod) {
      case "DAILY":
        newStartDate = format(startOfDay(currentDate), "yyyy-MM-dd");
        newEndDate = format(endOfDay(currentDate), "yyyy-MM-dd");
        break;
      case "WEEKLY":
        newStartDate = format(startOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
        newEndDate = format(endOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
        break;
      case "MONTHLY":
        newStartDate = format(startOfMonth(currentDate), "yyyy-MM-dd");
        newEndDate = format(endOfMonth(currentDate), "yyyy-MM-dd");
        break;
      case "YEARLY":
        newStartDate = format(startOfYear(currentDate), "yyyy-MM-dd");
        newEndDate = format(endOfYear(currentDate), "yyyy-MM-dd");
        break;
      default:
        return;
    }

    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set("startDate", newStartDate);
    newSearchParams.set("endDate", newEndDate);
    
    if (!searchParams.get("outletId")) {
      newSearchParams.set("outletId", outlets?.[0]?._id || "");
    }

    setSearchParams(newSearchParams);
  }, [currentDate, selectedTimePeriod, outlets, searchParams, setSearchParams]);

  const handlePrevious = () => {
    switch (selectedTimePeriod) {
      case "DAILY": setCurrentDate(prev => subDays(prev, 1)); break;
      case "WEEKLY": setCurrentDate(prev => subWeeks(prev, 1)); break;
      case "MONTHLY": setCurrentDate(prev => subMonths(prev, 1)); break;
      case "YEARLY": setCurrentDate(prev => subYears(prev, 1)); break;
      default: break;
    }
  };

  const handleNext = () => {
    switch (selectedTimePeriod) {
      case "DAILY": setCurrentDate(prev => addDays(prev, 1)); break;
      case "WEEKLY": setCurrentDate(prev => addWeeks(prev, 1)); break;
      case "MONTHLY": setCurrentDate(prev => addMonths(prev, 1)); break;
      case "YEARLY": setCurrentDate(prev => addYears(prev, 1)); break;
      default: break;
    }
  };

  const getDateLabel = () => {
    switch (selectedTimePeriod) {
      case "DAILY": return format(currentDate, "MMM d, yyyy");
      case "WEEKLY": return `${format(startOfWeek(currentDate, { weekStartsOn: 1 }), "MMM d")} - ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), "MMM d, yyyy")}`;
      case "MONTHLY": return format(currentDate, "MMMM yyyy");
      case "YEARLY": return format(currentDate, "yyyy");
      case "CUSTOM": return `${startDate} to ${endDate}`;
      default: return "";
    }
  };

  // ✅ FIX 3: Fully Dynamic Table Headers based on Report Type & Measure
  const getTableHeaders = (): TableHeader<any>[] => {
    // 1. Sales Summary -> Shows Invoices
    if (selectedReportType === 'SALES_SUMMARY') {
      return [
        { fieldName: 'invoiceNumber', headerName: 'Invoice No.', flex: 'flex-[1_1_0%]' },
        { fieldName: 'invoiceDate', headerName: 'Date', flex: 'flex-[1_1_0%]', render: (row: any) => row.invoiceDate ? formatZonedDate(new Date(row.invoiceDate)) : '-' },
        { fieldName: 'items', headerName: 'Items', flex: 'flex-[2_1_0%]', render: (row: any) => row.items?.map((i: any) => `${i.quantity}x ${i.itemName}`).join(', ') || '-' },
        { fieldName: 'totalAmount', headerName: 'Total Amount', flex: 'flex-[1_1_0%]', render: (row: any) => `R ${Number(row.totalAmount || 0).toFixed(2)}` },
        { fieldName: 'amountPaid', headerName: 'Amount Paid', flex: 'flex-[1_1_0%]', render: (row: any) => `R ${Number(row.amountPaid || 0).toFixed(2)}` },
      ];
    }

    // 2. Grouped Reports -> Shows Aggregated Data
    const headers: TableHeader<any>[] = [];

    // Primary Column (User Name, Customer Name, etc.)
    let primaryHeaderName = 'Name';
    if (selectedReportType === 'USER') primaryHeaderName = 'User Name';
    if (selectedReportType === 'OUTLET') primaryHeaderName = 'Outlet Name';
    if (selectedReportType === 'CUSTOMER') primaryHeaderName = 'Customer Name';
    if (selectedReportType === 'PROMOTION') primaryHeaderName = 'Promo Code';
    if (selectedReportType === 'CUSTOMER_GROUP') primaryHeaderName = 'Customer Group';
    if (selectedReportType === 'REGISTER') primaryHeaderName = 'Register';

    headers.push({
      fieldName: 'primaryName',
      headerName: primaryHeaderName,
      flex: 'flex-[1.5_1_0%]',
      render: (row: any) => {
        return row.user || row.customer || row.outlet || row.promotion || row.customerGroup || row.register || row._id || '-';
      }
    });

    // Measure Columns
    if (selectedMeasure === 'REVENUE' || selectedMeasure === 'ALL') {
      headers.push({
        fieldName: 'revenue', headerName: 'Revenue', flex: 'flex-[1_1_0%]',
        render: (row: any) => `R ${Number(row.revenue || 0).toFixed(2)}`
      });
    }

    if (selectedMeasure === 'SALE_COUNT' || selectedMeasure === 'ALL') {
      headers.push({
        fieldName: 'saleCount', headerName: 'Sale Count', flex: 'flex-[1_1_0%]',
        render: (row: any) => row.saleCount || 0
      });
    }

    if (selectedMeasure === 'AVG_SALE_VALUE' || selectedMeasure === 'ALL') {
      headers.push({
        fieldName: 'avgSaleValue', headerName: 'Avg. Sale Value', flex: 'flex-[1_1_0%]',
        render: (row: any) => `R ${Number(row.avgSaleValue || 0).toFixed(2)}`
      });
    }

    if (selectedMeasure === 'CUSTOMER_COUNT' || selectedMeasure === 'ALL') {
      headers.push({
        fieldName: 'customerCount', headerName: 'Customer Count', flex: 'flex-[1_1_0%]',
        render: (row: any) => row.customerCount || 0
      });
    }

    if (selectedMeasure === 'DISCOUNTED' || selectedMeasure === 'ALL') {
      headers.push({
        fieldName: 'discounted', headerName: 'Discounted', flex: 'flex-[1_1_0%]',
        render: (row: any) => `R ${Number(row.discounted || 0).toFixed(2)}`
      });
    }

    return headers;
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (!reportData.length) {
      alert("No data to export!");
      return;
    }

    const exportData = reportData.map((row: any) => {
      const flatRow: any = {};
      Object.keys(row).forEach(key => {
        if (typeof row[key] === 'object' && row[key] !== null) {
          flatRow[key] = JSON.stringify(row[key]);
        } else {
          flatRow[key] = row[key];
        }
      });
      return flatRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    worksheet["!cols"] = Object.keys(exportData[0]).map(() => ({ wch: 20 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "SalesReport");
    
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `SalesReport_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filters: FilterType[] = [
    {
      filterType: "single-select", label: "Report type", fieldName: "reportType", options: reportTypes,
      renderOption: (option) => option.label, isOptionEqualToSearchValue: (option, value) => option?.label?.toLowerCase()?.includes(value?.toLowerCase()),
    },
    {
      filterType: "single-select", label: "Outlet", fieldName: "outletId",
      options: [{ label: "All Outlets", value: "ALL" }, ...(outlets?.map((el: any) => ({ label: el?.name, value: el?._id })) || [])],
      renderOption: (option) => option.label, isOptionEqualToSearchValue: (option, value) => option?.label?.toLowerCase()?.includes(value?.toLowerCase()),
    },
    {
      filterType: "single-select", label: "Measure", fieldName: "measure", options: measureTypes,
      renderOption: (option) => option.label, isOptionEqualToSearchValue: (option, value) => option?.label?.toLowerCase()?.includes(value?.toLowerCase()),
    },
    ...(selectedTimePeriod === "CUSTOM" ? [{
      filterType: "date" as const, fieldName: "createdAt",
      dateFilterKeyOptions: [{ label: "Start Date", value: startDate }, { label: "End Date", value: endDate }],
    }] : []),
    {
      filterType: "single-select", label: "Comparison", fieldName: "comparison", options: comparisonTypes,
      renderOption: (option) => option.label, isOptionEqualToSearchValue: (option, value) => option?.label?.toLowerCase()?.includes(value?.toLowerCase()),
    },
    {
      filterType: "single-select", label: "Time Period", fieldName: "timePeriod", options: timePeriods,
      renderOption: (option) => option.label, isOptionEqualToSearchValue: (option, value) => option?.label?.toLowerCase()?.includes(value?.toLowerCase()),
    },
  ];

  return (
    <div className="flex flex-col h-full gap-2 p-4">
      <ATMPageHeader heading="Sales Report" buttonProps={{ label: 'Export Excel', onClick: handleExportExcel }} />

      <Authorization permission="SALES_LEDGER">
        <div className="bg-white rounded-xl border p-4">
          
          {selectedTimePeriod !== 'CUSTOM' && (
            <div className="flex items-center justify-between bg-gray-50 border rounded-xl px-4 py-3 mb-4">
              <button onClick={handlePrevious} className="border rounded px-3 py-1 hover:bg-gray-100">←</button>
              <div className="font-semibold text-lg">{getDateLabel()}</div>
              <button onClick={handleNext} className="border rounded px-3 py-1 hover:bg-gray-100">→</button>
            </div>
          )}

          <MOLFilterBar hideSearch={true} filters={filters} />

          <div className="mt-2 p-2 bg-gray-50 rounded border text-sm">
            <span className="font-medium">Active Filters: </span>
            Report: {reportTypes.find(r => r.value === selectedReportType)?.label} | 
            Outlet: {selectedOutlet === 'ALL' ? 'All Outlets' : outlets?.find(o => o._id === selectedOutlet)?.name || 'All'} | 
            Measure: {measureTypes.find(m => m.value === selectedMeasure)?.label} | 
            Comparison: {comparisonTypes.find(c => c.value === selectedComparison)?.label} | 
            Period: {getDateLabel()}
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mt-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-600">Total Revenue</p>
              <p className="text-xl font-bold">R {Number(totalRevenue).toFixed(2)}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-green-600">Total Sales</p>
              <p className="text-xl font-bold">{totalSalesCount}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-600">Total Profit</p>
              <p className="text-xl font-bold">R {Number(totalProfit).toFixed(2)}</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-600">Margin</p>
              <p className="text-xl font-bold">{totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100).toFixed(1) : 0}%</p>
            </div>
          </div>

          {/* Chart Section */}
          {chartData && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <ATMChart 
                data={chartData} 
                type={selectedReportType === 'OUTLET' ? 'bar' : 'line'}
                height={300}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { position: 'top' },
                    title: { display: true, text: `Sales Overview - ${selectedReportType}` },
                  },
                }}
              />
            </div>
          )}

          {/* Table Section */}
          <div className="flex flex-col overflow-auto border rounded border-slate-300 mt-4 always-scrollbar">
            <div className="flex-1">
              <MOLTable
                tableHeaders={getTableHeaders()}
                data={reportData || []}
                // ✅ FIX 4: Forcing string return to fix TS2322 error
                getKey={(item: any) => String(item?._id || item?.invoiceNumber || Math.random())}
                isLoading={isLoading}
              />
            </div>

            <ATMPagination
              totalPages={totalPages || 1}
              rowCount={totalData || 0}
              rows={reportData || []}
            />
          </div>
        </div>
      </Authorization>
    </div>
  );
};

export default SalesReportPage;